import { useState, useEffect, useCallback } from 'react';
import {
  Table,
  Card,
  Button,
  Space,
  Tag,
  Input,
  Select,
  Row,
  Col,
  Typography,
  message,
  Tooltip,
  Modal
} from 'antd';
import {
  SearchOutlined,
  EyeOutlined,
  EditOutlined,
  DeleteOutlined,
  SendOutlined,
  ReloadOutlined,
  PlusOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { cogsService } from '../services/cogsService';
import { dimensionsService } from '../services/dimensionsService';
import CostingRuleDetails from '../components/CostingRuleDetails';
import { useAuth } from '../hooks/useAuth';

const { Title, Text } = Typography;
const { Option } = Select;
const { confirm } = Modal;

const DraftRulesPage = () => {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0
  });
  const [filters, setFilters] = useState({ status: 'DRAFT' });
  const [dimensions, setDimensions] = useState({});
  const [selectedRuleId, setSelectedRuleId] = useState(null);
  const [detailsVisible, setDetailsVisible] = useState(false);
  
  const navigate = useNavigate();
  const { user } = useAuth();
  const userData = user?.data;

  // Load dimensions for filters
  const loadDimensions = useCallback(async () => {
    try {
      const dimensionData = await dimensionsService.getAllDimensions();
      setDimensions(dimensionData);
    } catch (error) {
      // Silently handle dimension loading error
    }
  }, []);

  // Load draft rules
  const loadRules = useCallback(async (page = 1, pageSize = 20, filterParams = {}) => {
    setLoading(true);
    try {
      const params = {
        page,
        limit: pageSize,
        status: 'DRAFT',
        ...filterParams
      };

      const response = await cogsService.getRules(params);
      setRules(response.rules || []);
      setPagination({
        current: page,
        pageSize,
        total: response.pagination?.total || 0
      });
    } catch (error) {
      message.error('Failed to load draft rules');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadDimensions();
    loadRules();
  }, [loadDimensions, loadRules]);

  // Handle table pagination change
  const handleTableChange = (paginationInfo) => {
    const { current, pageSize } = paginationInfo;
    loadRules(current, pageSize, filters);
  };

  // Format currency
  const formatCurrency = (amount, currency) => {
    if (!amount) return 'N/A';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency || 'USD'
    }).format(amount);
  };

  // Format date with timezone
  const formatDateWithTimezone = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZoneName: 'short'
    });
  };

  // Render fee structure
  const renderFeeStructure = (record) => {
    const fees = [];

    if (record.flatFee) {
      fees.push(`${formatCurrency(record.flatFee, record.feeCurrencyCode)}`);
    }

    if (record.percentageRate) {
      fees.push(`${record.percentageRate}%`);
    }

    if (record.minimumFee) {
      fees.push(`Min: ${formatCurrency(record.minimumFee, record.feeCurrencyCode)}`);
    }

    if (record.maximumFee) {
      fees.push(`Max: ${formatCurrency(record.maximumFee, record.feeCurrencyCode)}`);
    }

    if (fees.length > 0) return fees.join(' | ');

    // Fallback: show tiered pricing summary if no flat/percentage fees
    if (record.tieredPricing) {
      const tp = record.tieredPricing;
      let tierCount = 0;
      if (tp.tier1End || tp.tier1FixedFee || tp.tier1MDR) tierCount++;
      if (tp.tier2End || tp.tier2FixedFee || tp.tier2MDR) tierCount++;
      if (tp.tier3End || tp.tier3FixedFee || tp.tier3MDR) tierCount++;

      const parts = [`Tiered (${tierCount} tier${tierCount > 1 ? 's' : ''})`];
      if (tp.tierCountModel) parts.push(tp.tierCountModel);
      return parts.join(' | ');
    }

    return 'No fee structure';
  };

  // Handle rule details view
  const handleViewDetails = (ruleId) => {
    setSelectedRuleId(ruleId);
    setDetailsVisible(true);
  };

  // Handle edit rule
  const handleEditRule = (ruleId) => {
    const rule = rules.find(r => r.id === ruleId);
    const editMode = rule?.status === 'APPROVED' ? 'edit-as-new' : 'edit';
    navigate(`/costing-rules/${ruleId}/${editMode}`);
  };

  // Handle delete rule
  const handleDeleteRule = (rule) => {
    confirm({
      title: 'Delete Draft Rule',
      content: `Are you sure you want to delete this draft COGS rule for ${rule.entity?.name}?`,
      okText: 'Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        setActionLoading(true);
        try {
          await cogsService.deleteRule(rule.id);
          message.success('Draft rule deleted successfully');
          loadRules(pagination.current, pagination.pageSize, filters);
        } catch (error) {
          message.error('Failed to delete draft rule');
        } finally {
          setActionLoading(false);
        }
      }
    });
  };

  // Handle submit for approval
  const handleSubmitForApproval = (rule) => {
    confirm({
      title: 'Submit for Approval',
      content: `Are you sure you want to submit this COGS rule for approval?`,
      okText: 'Submit',
      okType: 'primary',
      cancelText: 'Cancel',
      onOk: async () => {
        setActionLoading(true);
        try {
          await cogsService.submitForApproval(rule.id, 'Submitted via draft rules page');
          message.success('Rule submitted for approval successfully');
          loadRules(pagination.current, pagination.pageSize, filters);
        } catch (error) {
          message.error('Failed to submit rule for approval');
        } finally {
          setActionLoading(false);
        }
      }
    });
  };

  // Apply filters
  const handleFilterChange = (key, value) => {
    const newFilters = { ...filters, [key]: value };
    if (!value) {
      delete newFilters[key];
    }
    setFilters(newFilters);
    loadRules(1, pagination.pageSize, newFilters);
  };

  // Check if user can edit/delete rule
  const canEditDelete = (rule) => {
    if (!userData) return false;
    if (userData.role === 'Admin') return true;
    return rule.proposerId === userData.id;
  };

  // Render costing details based on available data
  const renderCostingDetails = (record) => {
    const details = [];

    // Payout-related details
    if (record.payoutRailName) {
      details.push(`Rail: ${record.payoutRailName}`);
    }
    if (record.payoutConnectorName) {
      details.push(`Connector: ${record.payoutConnectorName}`);
    }

    // Card-related details
    if (record.acquirerName) {
      details.push(`Acquirer: ${record.acquirerName}`);
    }
    if (record.cardBrandName) {
      details.push(`Brand: ${record.cardBrandName}`);
    }
    if (record.cardType && record.cardType !== 'N/A') {
      details.push(`Type: ${record.cardType}`);
    }
    if (record.cardTier) {
      details.push(`Tier: ${record.cardTier}`);
    }
    if (record.mccs && record.mccs.length > 0) {
      details.push(`MCCs: ${record.mccs.map(m => m.mcc).join(', ')}`);
    } else if (record.mcc) {
      details.push(`MCCs: ${record.mcc}`);
    }

    // Other details
    if (record.industryName) {
      details.push(`Industry: ${record.industryName}`);
    }
    if (record.flow) {
      details.push(`Flow: ${record.flow}`);
    }
    if (record.processor) {
      details.push(`Processor: ${record.processor}`);
    }
    if (record.businessId) {
      details.push(`Business: ${record.businessId}`);
    }

    return details.length > 0 ? (
      <div>
        {details.map((detail, index) => (
          <div key={index} style={{ fontSize: '11px' }}>
            <Text type="secondary">{detail}</Text>
          </div>
        ))}
      </div>
    ) : 'N/A';
  };

  // Table columns configuration
  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
      sorter: true,
    },
    {
      title: 'Entity',
      dataIndex: ['entity', 'name'],
      key: 'entity',
      ellipsis: true,
      render: (text, record) => (
        <div>
          <Text strong>{text || 'N/A'}</Text>
          {record.entity?.country && (
            <div><Tag size="small">{record.entity.country}</Tag></div>
          )}
        </div>
      ),
    },
    {
      title: 'Product',
      dataIndex: ['product', 'name'],
      key: 'product',
      ellipsis: true,
    },
    {
      title: 'Pricing Scheme',
      dataIndex: ['scheme'],
      key: 'scheme',
      ellipsis: true,
      render: (scheme) => {
        if (!scheme) return 'N/A';
        if (scheme.code && scheme.description) {
          return `${scheme.code} - ${scheme.description}`;
        }
        return scheme.code || scheme.description || 'N/A';
      },
    },
    {
      title: 'Fee Structure',
      key: 'feeStructure',
      ellipsis: true,
      render: (_, record) => (
        <Text code style={{ fontSize: '12px' }}>
          {renderFeeStructure(record)}
        </Text>
      ),
    },
    {
      title: 'Costing Details',
      key: 'costingDetails',
      width: 200,
      ellipsis: true,
      render: (_, record) => renderCostingDetails(record),
    },
    {
      title: 'Currency',
      dataIndex: 'feeCurrencyCode',
      key: 'currency',
      width: 80,
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
        <div style={{ padding: 8 }}>
          <Input
            placeholder="Search currency"
            value={selectedKeys[0]}
            onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
            onPressEnter={() => confirm()}
            style={{ marginBottom: 8, display: 'block' }}
          />
          <Space>
            <Button
              type="primary"
              onClick={() => confirm()}
              icon={<SearchOutlined />}
              size="small"
              style={{ width: 90 }}
            >
              Search
            </Button>
            <Button
              onClick={() => {
                clearFilters();
                confirm();
              }}
              size="small"
              style={{ width: 90 }}
            >
              Reset
            </Button>
          </Space>
        </div>
      ),
      filterIcon: (filtered) => (
        <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />
      ),
      onFilter: (value, record) =>
        record.feeCurrencyCode
          ? record.feeCurrencyCode.toString().toLowerCase().includes(value.toLowerCase())
          : false,
    },
    {
      title: 'Proposer',
      dataIndex: ['proposer', 'email'],
      key: 'proposer',
      ellipsis: true,
    },
    {
      title: 'Created',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 150,
      render: (date) => formatDateWithTimezone(date),
    },
    {
      title: 'Updated',
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      width: 150,
      render: (date) => formatDateWithTimezone(date),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 180,
      fixed: 'right',
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="View Details">
            <Button
              type="text"
              icon={<EyeOutlined />}
              onClick={() => handleViewDetails(record.id)}
            />
          </Tooltip>
          {canEditDelete(record) && (
            <>
              <Tooltip title="Edit">
                <Button
                  type="text"
                  icon={<EditOutlined />}
                  onClick={() => handleEditRule(record.id)}
                />
              </Tooltip>
              <Tooltip title="Submit for Approval">
                <Button
                  type="text"
                  icon={<SendOutlined />}
                  style={{ color: '#1890ff' }}
                  loading={actionLoading}
                  onClick={() => handleSubmitForApproval(record)}
                />
              </Tooltip>
              <Tooltip title="Delete">
                <Button
                  type="text"
                  icon={<DeleteOutlined />}
                  danger
                  loading={actionLoading}
                  onClick={() => handleDeleteRule(record)}
                />
              </Tooltip>
            </>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: '24px' }}>
      <Card>
        <div style={{ marginBottom: '24px' }}>
          <Title level={2}>Draft COGS Rules</Title>
          <Text type="secondary">
            Manage your draft COGS rules - edit, submit for approval, or delete
          </Text>
        </div>

        {/* Action Bar */}
        <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
          <Col span={6}>
            <Input
              placeholder="Search by description, ID, etc."
              prefix={<SearchOutlined />}
              onChange={(e) => handleFilterChange('search', e.target.value)}
              allowClear
            />
          </Col>
          <Col span={4}>
            <Select
              placeholder="Entity"
              style={{ width: '100%' }}
              onChange={(value) => handleFilterChange('entityId', value)}
              allowClear
            >
              {dimensions.entities?.map(entity => (
                <Option key={entity.id} value={entity.id}>
                  {entity.name}
                </Option>
              ))}
            </Select>
          </Col>
          <Col span={4}>
            <Select
              placeholder="Product"
              style={{ width: '100%' }}
              onChange={(value) => handleFilterChange('productId', value)}
              allowClear
            >
              {dimensions.products?.map(product => (
                <Option key={product.id} value={product.id}>
                  {product.name}
                </Option>
              ))}
            </Select>
          </Col>
          <Col span={4}>
            <Select
              placeholder="Currency"
              style={{ width: '100%' }}
              onChange={(value) => handleFilterChange('feeCurrencyCode', value)}
              allowClear
            >
              {dimensions.currencies?.map(currency => (
                <Option key={currency.code} value={currency.code}>
                  {currency.code}
                </Option>
              ))}
            </Select>
          </Col>
          <Col span={6}>
            <Space>
              <Button
                icon={<ReloadOutlined />}
                onClick={() => loadRules(pagination.current, pagination.pageSize, filters)}
              >
                Refresh
              </Button>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => navigate('/costing-rules/create')}
              >
                Create New Rule
              </Button>
            </Space>
          </Col>
        </Row>

        {/* Stats */}
        <div style={{ marginBottom: '16px' }}>
          <Text type="secondary">
            {pagination.total} draft rule{pagination.total !== 1 ? 's' : ''}
          </Text>
        </div>

        {/* Main Table */}
        <Table
          columns={columns}
          dataSource={rules}
          rowKey="id"
          loading={loading}
          pagination={{
            ...pagination,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} of ${total} draft rules`,
          }}
          onChange={handleTableChange}
          scroll={{ x: 1300 }}
          size="small"
        />
      </Card>

      {/* Rule Details Modal */}
      <CostingRuleDetails
        ruleId={selectedRuleId}
        visible={detailsVisible}
        onClose={() => {
          setDetailsVisible(false);
          setSelectedRuleId(null);
        }}
        onEdit={handleEditRule}
        onDelete={(rule) => {
          setDetailsVisible(false);
          setSelectedRuleId(null);
          handleDeleteRule(rule);
        }}
        refreshData={() => loadRules(pagination.current, pagination.pageSize, filters)}
      />
    </div>
  );
};

export default DraftRulesPage;
